'use client';

import { httpMethods } from '@/constants/http-methods';
import {
  PostShoppingCartConcessionRequest,
  PostShoppingCartTicketRequest,
  PutShoppingCartConcessionRequest,
  PutShoppingCartTicketRequest,
} from '@/types/checkout';
import { ShoppingCartReview } from '@/types/shopping-cart';
import { commonFetch } from '@/utils/common-fetch';
import { ShoppingCart } from '@cinemark/checkout-api-client';

class ShoppingCartApiService {
  private readonly cartUrl = '/papi/checkout/cart';
  private readonly cartReviewUrl = '/papi/checkout/cart/review';
  private readonly concessionsUrl = '/papi/checkout/cart/concessions';
  private readonly familyEntertainmentCenterConcessionsUrl = '/papi/checkout/cart/family-entertainment-center-concessions';
  private readonly ticketsUrl = '/papi/checkout/cart/tickets';

  async getCart(signal?: AbortSignal): Promise<ShoppingCart | null> {
    const response = await commonFetch<ShoppingCart>(this.cartUrl, { signal });
    return response.data;
  }

  async getCartReview(signal?: AbortSignal): Promise<ShoppingCartReview | null> {
    const response = await commonFetch<ShoppingCartReview>(this.cartReviewUrl, { signal });
    return response.data;
  }

  async clearCart(): Promise<void> {
    await commonFetch(this.cartUrl, { method: httpMethods.delete });
  }

  async addTicket(ticket: PostShoppingCartTicketRequest): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(this.ticketsUrl, {
      body: JSON.stringify(ticket),
      method: httpMethods.post,
    });

    return response.data;
  }

  async updateTicket(ticketId: string, ticket: PutShoppingCartTicketRequest): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(`${this.ticketsUrl}/${ticketId}`, {
      body: JSON.stringify(ticket),
      method: httpMethods.put,
    });

    return response.data;
  }

  async removeTicket(ticketId: string, showtimeId: number): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(`${this.ticketsUrl}/${ticketId}?showtimeId=${showtimeId}`, {
      method: httpMethods.delete,
    });

    return response.data;
  }

  async removeShowtime(showtimeId: number): Promise<ShoppingCartReview> {
    const response = await commonFetch<ShoppingCartReview>(`${this.cartReviewUrl}/showtimes/${showtimeId}`, {
      method: httpMethods.delete,
    });

    return response.data;
  }

  async addConcession(concession: PostShoppingCartConcessionRequest): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(this.concessionsUrl, {
      body: JSON.stringify(concession),
      method: httpMethods.post,
    });

    return response.data;
  }

  async updateConcession(concessionId: string, concession: PutShoppingCartConcessionRequest): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(`${this.concessionsUrl}/${concessionId}`, {
      body: JSON.stringify(concession),
      method: httpMethods.put,
    });

    return response.data;
  }

  async removeConcession(concessionId: string, showtimeId: number): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(`${this.concessionsUrl}/${concessionId}?showtimeId=${showtimeId}`, {
      method: httpMethods.delete,
    });

    return response.data;
  }

  async addFamilyEntertainmentCenterConcession(concession: PostShoppingCartConcessionRequest): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(this.familyEntertainmentCenterConcessionsUrl, {
      body: JSON.stringify(concession),
      method: httpMethods.post,
    });

    return response.data;
  }

  async updateFamilyEntertainmentCenterConcession(concessionId: string, concession: PutShoppingCartConcessionRequest): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(`${this.familyEntertainmentCenterConcessionsUrl}/${concessionId}`, {
      body: JSON.stringify(concession),
      method: httpMethods.put,
    });

    return response.data;
  }

  async removeFamilyEntertainmentCenterConcession(concessionId: string): Promise<ShoppingCart> {
    const response = await commonFetch<ShoppingCart>(`${this.familyEntertainmentCenterConcessionsUrl}/${concessionId}`, {
      method: httpMethods.delete,
    });

    return response.data;
  }
}

export const shoppingCartApiService = new ShoppingCartApiService();
